const PRECISION = 1e-6;

export function createMatrix(elements) {
  return {
    elements,
    rows() {
      return elements.length;
    },
    e(row, col) {
      return elements[row - 1][col - 1];
    },
    multiply(other) {
      if (typeof other === 'number') {
        const result = elements.map((row) =>
          row.map((value) => value * other),
        );
        return createMatrix(result);
      }

      const a = elements;
      const b = other.elements;

      if (a[0].length !== b.length) {
        return null;
      }

      const result = [];
      for (let i = 0; i < a.length; i++) {
        result[i] = [];
        for (let j = 0; j < b[0].length; j++) {
          let sum = 0;
          for (let k = 0; k < a[0].length; k++) {
            sum += a[i][k] * b[k][j];
          }
          result[i][j] = sum;
        }
      }

      return createMatrix(result);
    },
    inverse() {
      const size = elements.length;

      for (let i = 0; i < size; i++) {
        if (elements[i].length !== size) {
          return null;
        }
      }

      if (isSingular(elements)) {
        return null;
      }

      const augmented = [];
      for (let i = 0; i < size; i++) {
        augmented[i] = [];
        for (let j = 0; j < size; j++) {
          augmented[i][j] = elements[i][j];
        }
        for (let j = 0; j < size; j++) {
          augmented[i][size + j] = i === j ? 1 : 0;
        }
      }

      const triangularMatrix = toRightTriangular(augmented);
      const triElements = triangularMatrix.elements;
      const totalCols = triElements[0].length;
      const inverseElements = [];

      for (let row = size - 1; row >= 0; row--) {
        const pivot = triElements[row][row];
        const newRow = [];
        const invRow = [];

        for (let col = 0; col < totalCols; col++) {
          const value = triElements[row][col] / pivot;
          newRow.push(value);
          if (col >= size) {
            invRow.push(value);
          }
        }

        inverseElements[row] = invRow;
        triElements[row] = newRow;

        for (let i = 0; i < row; i++) {
          const factor = triElements[i][row];
          const updatedRow = [];
          for (let col = 0; col < totalCols; col++) {
            updatedRow.push(triElements[i][col] - newRow[col] * factor);
          }
          triElements[i] = updatedRow;
        }
      }

      return createMatrix(inverseElements);
    },
    toRightTriangular() {
      return toRightTriangular(elements);
    },
    round(step) {
      const result = [];
      for (let i = 0; i < elements.length; i++) {
        result[i] = [];
        for (let j = 0; j < elements[i].length; j++) {
          const value = elements[i][j];
          if (step instanceof Array) {
            result[i][j] = Math.round(value / step[i]) * step[i];
          } else if (step === undefined) {
            result[i][j] = Math.round(value);
          } else {
            result[i][j] = Math.round(value / step) * step;
          }
        }
      }
      return createMatrix(result);
    },
  };
}

function toRightTriangular(elements) {
  const result = elements.map((row) => [...row]);
  const rows = result.length;
  const cols = result[0].length;

  for (let r = 0; r < rows; r++) {
    if (Math.abs(result[r][r]) <= PRECISION) {
      for (let h = r + 1; h < rows; h++) {
        if (Math.abs(result[h][r]) > PRECISION) {
          const newRow = [];
          for (let c = 0; c < cols; c++) {
            newRow.push(result[r][c] + result[h][c]);
          }
          result[r] = newRow;
          break;
        }
      }
    }

    if (Math.abs(result[r][r]) > PRECISION) {
      for (let h = r + 1; h < rows; h++) {
        const factor = result[h][r] / result[r][r];
        const newRow = [];
        for (let c = 0; c < cols; c++) {
          newRow.push(c <= r ? 0 : result[h][c] - result[r][c] * factor);
        }
        result[h] = newRow;
      }
    }
  }

  return createMatrix(result);
}

function determinant(elements) {
  const size = elements.length;

  for (let i = 0; i < size; i++) {
    if (elements[i].length !== size) {
      return null;
    }
  }

  const triangular = toRightTriangular(elements);
  const tri = triangular.elements;
  let det = tri[0][0];

  for (let i = 1; i < size; i++) {
    det *= tri[i][i];
  }

  return det;
}

function isSingular(elements) {
  return Math.abs(determinant(elements)) <= PRECISION;
}

export function arrayToColumnVector(values) {
  return createMatrix(values.map((value) => [value]));
}

export function sumColumn(matrix) {
  let sum = 0;
  for (let i = 0; i < matrix.rows(); i++) {
    sum += matrix.e(i + 1, 1);
  }
  return sum;
}

export function extractColumn(matrix, colIndex = 1) {
  const result = [];
  for (let i = 0; i < matrix.rows(); i++) {
    result[i] = matrix.e(i + 1, colIndex);
  }
  return result;
}

export function solveLinearSystem(matrix, vector) {
  const inverse = matrix.inverse();
  return inverse ? inverse.multiply(vector) : null;
}

export function fallbackSolver(matrix) {
  const triangular = matrix.toRightTriangular();
  const rows = matrix.rows();
  const values = new Array(rows);

  values[rows - 1] = [1];

  for (let n = rows - 1; n > 0; n--) {
    let sum = 0;
    for (let a = rows; a > n; a--) {
      sum = sum - triangular.e(n, a) * values[a - 1][0];
    }
    values[n - 1] = [sum / triangular.e(n, n)];
  }

  return createMatrix(values);
}

export function roundArray(values, step = 1) {
  const matrix = createMatrix(values.map((value) => [value]));
  const rounded = matrix.round(step);
  return extractColumn(rounded);
}

export function normalizeValue(value) {
  if (value !== null) {
    return value.toString().replace(/,/g, '.').replace(/[\s_]/g, '');
  }
}
